/**
 * Functional Assessment History
 * 
 * Displays patient's functional status assessment history with:
 * - Timeline view of all assessments
 * - Score comparisons over time
 * - Alert history
 * - Export capability
 */

import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Activity, Clock, TrendingDown, TrendingUp, Minus,
  AlertTriangle, CheckCircle, FileText, Download,
  Loader2, RefreshCw, User, Calendar
} from "lucide-react";
import { toast } from "sonner";
import FunctionalStatusTrend from "./FunctionalStatusTrend";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function FunctionalAssessmentHistory({
  patientId,
  patientName,
  isDark = true,
  compact = false
}) {
  const token = localStorage.getItem("token");
  
  // States
  const [assessments, setAssessments] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("timeline");

  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      if (!patientId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        
        // Fetch assessments
        const assessRes = await fetch(
          `${API_URL}/api/lcd-criteria/patient/${patientId}/evaluations?limit=50`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (assessRes.ok) {
          const data = await assessRes.json();
          setAssessments(data.evaluations || []);
        }
        
        // Fetch alerts
        const alertsRes = await fetch(
          `${API_URL}/api/lcd-alerts/patient/${patientId}?status=all&limit=20`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (alertsRes.ok) {
          const data = await alertsRes.json();
          setAlerts(data.alerts || []);
        }
        
      } catch (error) {
        console.error("Failed to fetch history:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [patientId, token]);

  // Get change indicator
  const getChangeIndicator = (current, previous) => {
    if (!previous) return null;
    const change = current - previous;
    if (change === 0) return <Minus className="w-4 h-4 text-slate-400" />;
    if (change > 0) return <TrendingUp className="w-4 h-4 text-emerald-400" />;
    return <TrendingDown className="w-4 h-4 text-red-400" />;
  };

  // Export history
  const exportHistory = () => {
    const data = assessments.map(a => ({
      date: a.created_at || a.evaluated_at,
      kps: a.functional_assessment?.kps_score,
      pps: a.functional_assessment?.pps_score,
      fast: a.functional_assessment?.fast_stage,
      nyha: a.functional_assessment?.nyha_class,
      eligible: a.hospice_eligible,
      notes: a.notes
    }));
    
    const csv = [
      ["Date", "KPS", "PPS", "FAST", "NYHA", "Eligible", "Notes"],
      ...data.map(d => [
        new Date(d.date).toLocaleDateString(),
        d.kps || "",
        d.pps || "",
        d.fast || "",
        d.nyha || "",
        d.eligible ? "Yes" : "No",
        d.notes || ""
      ])
    ].map(row => row.join(",")).join("\n");
    
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `functional-history-${patientId}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("History exported");
  };

  if (compact) {
    return (
      <Card className={isDark ? "bg-slate-900 border-slate-800" : "bg-white"} data-testid="assessment-history-compact">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <Clock className="w-4 h-4 text-blue-400" />
            Assessment History
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-4 text-center">
              <Loader2 className="w-5 h-5 animate-spin mx-auto text-slate-400" />
            </div>
          ) : assessments.length > 0 ? (
            <div className="space-y-2">
              {assessments.slice(0, 5).map((assessment, idx) => {
                const kps = assessment.functional_assessment?.kps_score;
                const prevKps = assessments[idx + 1]?.functional_assessment?.kps_score;
                
                return (
                  <div 
                    key={idx}
                    className={`p-2 rounded-lg flex items-center justify-between ${
                      isDark ? "bg-slate-800" : "bg-gray-100"
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span className={`text-xs ${isDark ? "text-slate-400" : "text-gray-500"}`}>
                        {new Date(assessment.created_at || assessment.evaluated_at).toLocaleDateString()}
                      </span>
                      {getChangeIndicator(kps, prevKps)}
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-xs">KPS: {kps || "N/A"}</Badge>
                      {assessment.hospice_eligible && (
                        <Badge className="bg-amber-500/20 text-amber-400 text-xs">Eligible</Badge>
                      )}
                    </div>
                  </div>
                );
              })}
              {assessments.length > 5 && (
                <p className={`text-xs text-center ${isDark ? "text-slate-500" : "text-gray-400"}`}>
                  +{assessments.length - 5} more assessments
                </p>
              )}
            </div>
          ) : (
            <p className={`text-sm text-center py-4 ${isDark ? "text-slate-500" : "text-gray-400"}`}>
              No assessment history
            </p>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={isDark ? "bg-slate-900 border-slate-800" : "bg-white"} data-testid="assessment-history">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <Clock className="w-5 h-5 text-blue-400" />
              Functional Assessment History
              {patientName && (
                <span className={`text-sm font-normal ${isDark ? "text-slate-400" : "text-gray-500"}`}>
                  - {patientName}
                </span>
              )}
            </CardTitle>
            <CardDescription>
              Complete history of functional status assessments and eligibility alerts
            </CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={exportHistory}>
            <Download className="w-4 h-4 mr-2" />
            Export
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className={isDark ? "bg-slate-800" : ""}>
            <TabsTrigger value="timeline">Timeline</TabsTrigger>
            <TabsTrigger value="chart">Trend Chart</TabsTrigger>
            <TabsTrigger value="alerts">Alerts ({alerts.length})</TabsTrigger>
          </TabsList>

          {/* Timeline Tab */}
          <TabsContent value="timeline" className="mt-4">
            {loading ? (
              <div className="py-8 text-center">
                <Loader2 className="w-8 h-8 animate-spin mx-auto text-slate-400" />
              </div>
            ) : assessments.length > 0 ? (
              <ScrollArea className="h-96">
                <div className="relative pl-8">
                  {/* Timeline line */}
                  <div className={`absolute left-3 top-0 bottom-0 w-0.5 ${isDark ? "bg-slate-700" : "bg-gray-200"}`} />
                  
                  {assessments.map((assessment, idx) => {
                    const kps = assessment.functional_assessment?.kps_score;
                    const pps = assessment.functional_assessment?.pps_score;
                    const fast = assessment.functional_assessment?.fast_stage;
                    const nyha = assessment.functional_assessment?.nyha_class;
                    const prevKps = assessments[idx + 1]?.functional_assessment?.kps_score;
                    const change = prevKps ? kps - prevKps : 0;
                    
                    return (
                      <div key={idx} className="relative pb-6">
                        {/* Timeline dot */}
                        <div className={`absolute left-0 w-6 h-6 rounded-full flex items-center justify-center ${
                          assessment.hospice_eligible 
                            ? "bg-amber-500/20 border-2 border-amber-500" 
                            : isDark ? "bg-slate-800 border-2 border-slate-600" : "bg-white border-2 border-gray-300"
                        }`}>
                          {assessment.hospice_eligible ? (
                            <AlertTriangle className="w-3 h-3 text-amber-400" />
                          ) : (
                            <Activity className="w-3 h-3 text-blue-400" />
                          )}
                        </div>
                        
                        {/* Content */}
                        <div className={`ml-6 p-4 rounded-lg ${isDark ? "bg-slate-800" : "bg-gray-100"}`}>
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <Calendar className="w-4 h-4 text-slate-400" />
                              <span className={`text-sm font-medium ${isDark ? "text-white" : "text-gray-900"}`}>
                                {new Date(assessment.created_at || assessment.evaluated_at).toLocaleDateString()}
                              </span>
                              <span className={`text-xs ${isDark ? "text-slate-500" : "text-gray-400"}`}>
                                {new Date(assessment.created_at || assessment.evaluated_at).toLocaleTimeString()}
                              </span>
                            </div>
                            {assessment.hospice_eligible && (
                              <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30">
                                Hospice Eligible
                              </Badge>
                            )}
                          </div>
                          
                          <div className="grid grid-cols-4 gap-3">
                            {kps !== undefined && (
                              <div className={`p-2 rounded ${isDark ? "bg-slate-900" : "bg-white"}`}>
                                <p className={`text-xs ${isDark ? "text-slate-400" : "text-gray-500"}`}>KPS</p>
                                <div className="flex items-center gap-1">
                                  <p className={`text-lg font-bold ${kps <= 70 ? "text-amber-400" : "text-blue-400"}`}>
                                    {kps}
                                  </p>
                                  {change !== 0 && (
                                    <span className={`text-xs ${change < 0 ? "text-red-400" : "text-emerald-400"}`}>
                                      ({change > 0 ? "+" : ""}{change})
                                    </span>
                                  )}
                                </div>
                              </div>
                            )}
                            {pps !== undefined && (
                              <div className={`p-2 rounded ${isDark ? "bg-slate-900" : "bg-white"}`}>
                                <p className={`text-xs ${isDark ? "text-slate-400" : "text-gray-500"}`}>PPS</p>
                                <p className={`text-lg font-bold ${pps <= 70 ? "text-amber-400" : "text-purple-400"}`}>
                                  {pps}%
                                </p>
                              </div>
                            )}
                            {fast && (
                              <div className={`p-2 rounded ${isDark ? "bg-slate-900" : "bg-white"}`}>
                                <p className={`text-xs ${isDark ? "text-slate-400" : "text-gray-500"}`}>FAST</p>
                                <p className={`text-lg font-bold ${["7c","7d","7e","7f"].includes(fast.toLowerCase()) ? "text-amber-400" : "text-slate-300"}`}>
                                  {fast}
                                </p>
                              </div>
                            )}
                            {nyha && (
                              <div className={`p-2 rounded ${isDark ? "bg-slate-900" : "bg-white"}`}>
                                <p className={`text-xs ${isDark ? "text-slate-400" : "text-gray-500"}`}>NYHA</p>
                                <p className={`text-lg font-bold ${nyha === "IV" ? "text-amber-400" : "text-slate-300"}`}>
                                  {nyha}
                                </p>
                              </div>
                            )}
                          </div>
                          
                          {assessment.notes && (
                            <p className={`text-sm mt-2 ${isDark ? "text-slate-400" : "text-gray-500"}`}>
                              {assessment.notes}
                            </p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </ScrollArea>
            ) : (
              <div className="py-12 text-center">
                <Activity className={`w-12 h-12 mx-auto mb-3 ${isDark ? "text-slate-600" : "text-gray-300"}`} />
                <p className={`text-sm ${isDark ? "text-slate-400" : "text-gray-500"}`}>
                  No functional assessments recorded for this patient
                </p>
              </div>
            )}
          </TabsContent>

          {/* Chart Tab */}
          <TabsContent value="chart" className="mt-4">
            <FunctionalStatusTrend 
              patientId={patientId}
              patientName={patientName}
              isDark={isDark}
              showPredictions={true}
            />
          </TabsContent>

          {/* Alerts Tab */}
          <TabsContent value="alerts" className="mt-4">
            {alerts.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow className={isDark ? "border-slate-800" : ""}>
                    <TableHead>Date</TableHead>
                    <TableHead>Alert Type</TableHead>
                    <TableHead>Value</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {alerts.map((alert, idx) => (
                    <TableRow key={idx} className={isDark ? "border-slate-800" : ""}>
                      <TableCell className="text-sm">
                        {new Date(alert.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{alert.title}</Badge>
                      </TableCell>
                      <TableCell className="font-mono">{alert.current_value}</TableCell>
                      <TableCell>
                        <Badge className={
                          alert.acknowledged 
                            ? "bg-emerald-500/20 text-emerald-400" 
                            : alert.status === "dismissed"
                              ? "bg-slate-500/20 text-slate-400"
                              : "bg-amber-500/20 text-amber-400"
                        }>
                          {alert.acknowledged ? "Acknowledged" : alert.status === "dismissed" ? "Dismissed" : "Active"}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="py-12 text-center">
                <CheckCircle className={`w-12 h-12 mx-auto mb-3 ${isDark ? "text-slate-600" : "text-gray-300"}`} />
                <p className={`text-sm ${isDark ? "text-slate-400" : "text-gray-500"}`}>
                  No eligibility alerts for this patient
                </p>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}
